import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import fr from './locales/fr.json';
import en from './locales/en.json';
import de from './locales/de.json';
import it from './locales/it.json';

export const SUPPORTED_LOCALES = ['fr', 'en', 'de', 'it'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  fr: 'Français',
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
};

export function isSupportedLocale(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// Read the persisted user.language directly out of the Zustand-persisted auth
// store. We do this *before* i18n.init runs so the very first render uses the
// authoritative DB-stored locale instead of whatever lingered in localStorage's
// i18next cache (which can drift across browser closes / multi-account use).
function readUserLanguage(): Locale | null {
  try {
    const raw = localStorage.getItem('trackfleet-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { user?: { language?: string } } };
    const lang = parsed?.state?.user?.language;
    return isSupportedLocale(lang) ? lang : null;
  } catch {
    return null;
  }
}

const initialLng = readUserLanguage() ?? undefined;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: fr },
      en: { translation: en },
      de: { translation: de },
      it: { translation: it },
    },
    // If the persisted auth store has a language, use it as the starting point.
    // Otherwise fall back to the detector chain (localStorage → navigator → htmlTag).
    lng: initialLng,
    fallbackLng: 'fr',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true, // 'fr-FR' → 'fr'
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'trackfleet.locale',
      caches: ['localStorage'],
    },
  });

export default i18n;
